import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {BioentityLinkSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

/**
 * Helpers for translating between the two TypedValue shapes used for parameters whose
 * confirmation method is `PLATE_MAP`:
 *
 * - UI: a single TypedValue carrying N sub-values (an `ArrayTypedValue` for data parameters,
 *   or a multi-link `BIOENTITY_LINK` for material inputs).
 * - REST/DB: one TypedValue per `values[]` entry, each representing a single plate-map slot.
 *
 * Empty containers are preserved as one entry so the parameter still appears in the payload.
 */

/** Expand a multi-value TypedValue into one entry per sub-value. Passes through scalars. */
export function expandTypedValueForMultiValuePlateMap(typedValue: TypedValue): Array<TypedValue> {
  let values: Array<TypedValue> = [];
  if (typedValue.type === BaseTypedValueType.ARRAY) {
    values = typedValue.rawValue;
  } else if (typedValue.type === FieldType.BIOENTITY_LINK) {
    values = typedValue.targetFiles.map((targetFile) => ({
      ...typedValue,
      targetFiles: [targetFile],
    }));
  }
  return values.length ? values : [typedValue];
}

/**
 * Collapse a list of stored TypedValues back into the single multi-value TypedValue the UI
 * cell editor expects. All-`BIOENTITY_LINK` lists merge into one multi-link value (non-
 * `targetFiles` fields taken from the first entry). Mixed-type lists wrap in an `ArrayTypedValue`.
 */
export function collapseTypedValuesForMultiValuePlateMap(
  typedValues: ReadonlyArray<TypedValue | null | undefined>
): TypedValue | null {
  const nonNullValues = typedValues.filter((tv): tv is TypedValue => tv != null);
  if (nonNullValues.length === 0) {
    return null;
  }
  if (nonNullValues.length === 1) {
    return nonNullValues[0];
  }
  const bioentityLinks = filterBioentityLinks(nonNullValues);
  if (bioentityLinks != null) {
    const first = bioentityLinks[0];
    return {
      ...first,
      targetFiles: bioentityLinks.flatMap((tv) => tv.targetFiles),
    };
  }
  return {
    type: BaseTypedValueType.ARRAY,
    rawValue: nonNullValues,
  };
}

function isBioentityLinkTypedValue(tv: TypedValue): tv is BioentityLinkSchemaFieldTypedValue {
  return tv.type === FieldType.BIOENTITY_LINK;
}

/** Returns the narrowed list if every entry is a BIOENTITY_LINK, else null. */
function filterBioentityLinks(
  typedValues: ReadonlyArray<TypedValue>
): ReadonlyArray<BioentityLinkSchemaFieldTypedValue> | null {
  const result: Array<BioentityLinkSchemaFieldTypedValue> = [];
  for (const tv of typedValues) {
    if (!isBioentityLinkTypedValue(tv)) {
      return null;
    }
    result.push(tv);
  }
  return result;
}

/**
 * Expand a UI data-parameter cell into one or more REST row entries. An unset cell emits a
 * single entry with a null value so the parameter is still represented in the payload.
 */
export function expandPlanDataParameterValuesForRest(
  typedValue: TypedValue | null | undefined
): Array<{value: TypedValue | null}> {
  if (typedValue == null) {
    return [{value: null}];
  }
  return expandTypedValueForMultiValuePlateMap(typedValue).map((tv) => ({value: tv}));
}

/**
 * Expand a UI material-input cell (material + amount) into one or more REST row entries.
 * Only the material side can be multi-value; the amount is duplicated across expanded entries.
 */
export function expandPlanMaterialInputValuesForRest(
  materialTypedValue: TypedValue | null | undefined,
  amountTypedValue: TypedValue | null | undefined
): Array<{value: {material: TypedValue | null; amount: TypedValue | null}}> {
  const amount = amountTypedValue ?? null;
  if (materialTypedValue == null) {
    return [{value: {material: null, amount}}];
  }
  return expandTypedValueForMultiValuePlateMap(materialTypedValue).map((materialTv) => ({
    value: {material: materialTv, amount},
  }));
}
